// Shared, standardized destination-search hook.
//
// One debounce + gate + cache implementation for every destination typeahead
// in the app (GlobalSearchScreen, AIScreen, AIAccordionScreen, …):
//   • 300ms debounce (debounceMs) so keystrokes don't each hit the API.
//   • >= minLength (default 1) character gate — the city index is in memory, so
//     a single letter can return a useful prefix match without a paid call.
//   • empty query still fetches the default/popular list (fetchEmpty=true)
//     so screens seeded with "Popular" keep working.
//   • results are cached by raw query so retyping an earlier search is instant.
//   • a successful non-empty search emits search_submitted with `surface`
//     (default 'debounced') so callers can label where the search came from.
//
// The hook is endpoint-agnostic: pass `fetcher(query) -> payload` and
// `select(payload) -> array`. Defaults to destinationsAPI.search. A `fallback`
// (called with the query) is used when the live call throws, e.g. AIScreen's
// curated popular list. `enabled=false` idles the hook (no request, empty
// results) — e.g. while a picker is closed.
import { useState, useEffect, useRef } from 'react';
import { destinations as destinationsAPI } from '../api';
import { trackActivity } from '../lib/activityEvents';

const cache = new Map();

export function clearDestSearchCache() {
  cache.clear();
}

export default function useDebouncedDestSearch(query, {
  minLength = 1,
  debounceMs = 300,
  fetchEmpty = true,
  enabled = true,
  surface = 'debounced',
  cacheScope = 'default',
  fetcher,
  select = (data) => (data && data.destinations) || [],
  fallback,
} = {}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const optsRef = useRef({ fetcher, select, fallback, minLength, debounceMs, fetchEmpty, surface, cacheScope });
  optsRef.current = { fetcher, select, fallback, minLength, debounceMs, fetchEmpty, surface, cacheScope };

  useEffect(() => {
    const q = (query ?? '').trim();

    // Gate below minLength; empty is allowed (popular list).
    const tooShort = q.length > 0 && q.length < optsRef.current.minLength;
    const skipEmpty = q.length === 0 && !optsRef.current.fetchEmpty;
    if (!enabled || tooShort || skipEmpty) {
      setResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    const cacheKey = `${optsRef.current.cacheScope}\u0000${q}`;
    if (cache.has(cacheKey)) {
      setResults(cache.get(cacheKey));
      setLoading(false);
      setError('');
      return undefined;
    }

    const { fetcher: doFetch, select: pick, fallback: onFail } = optsRef.current;
    const useDefault = doFetch ? false : true;

    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const data = useDefault ? await destinationsAPI.search(q) : await doFetch(q);
        const list = pick(data);
        cache.set(cacheKey, list);
        setResults(list);
        if (q) trackActivity('search', 'search_submitted', { query: q, surface: optsRef.current.surface });
      } catch {
        if (onFail) {
          const list = onFail(q);
          cache.set(cacheKey, list);
          setResults(list);
        } else {
          setResults([]);
          setError('Something went wrong. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    }, optsRef.current.debounceMs);

    return () => clearTimeout(timer);
  }, [query, enabled]);

  return { results, loading, error };
}
